import { AppDispatch } from "../../store";
import {
  setHandoverState,
  setSelectedAttendees,
  toggleShiftHandoverOption,
  setObservationNotes,
  setSelectedShift,
  setMiscObservationImage,
  setShiftHandoverImage,
  resetShiftHandoverImages,
  setSignatureFileName,
} from "../../store/slices/shiftHandOverSlice";
import { ShiftHandoverRecord } from "./fetchShiftHandoverData";
import { createApiClient, apiClient } from "../../api/apiClient";
import { formatUserName } from "../../utils/emailTouserName";
import { handleShiftHandoverErrorWithFriendlyMessage, SHIFTHANDOVER_API_NAMES } from "../../store/utils/shiftHandoverErrorHandler";

/**
 * Helper function for standardized logging
 * @param message - Log message
 * @param data - Optional data to log
 */
const logDebug = (message: string, data?: any): void => {
  if (process.env.NODE_ENV !== "production") {
    console.log(`[ShiftHandover] ${message}`, data !== undefined ? data : "");
  }
};

/**
 * Helper function for standardized error logging
 * @param message - Error message
 * @param error - Optional error object
 */
const logError = (message: string, error?: any): void => {
  console.error(`[ShiftHandover] ERROR: ${message}`, error || "");
};

/**
 * Function to update Redux state based on selected handover record
 * @param selectedHandover - Selected handover record
 * @param dispatch - Redux dispatch function
 */
export const updateShiftHandoverState = (
  selectedHandover: ShiftHandoverRecord | null,
  dispatch: AppDispatch
): void => {
  if (selectedHandover) {
    logDebug("Updating ShiftHandover state with:", selectedHandover);

    // Set handover state based on status
    if (selectedHandover.status) {
      const status = selectedHandover.status.toLowerCase().replace(" ", "-");
      if (
        status === "new" ||
        status === "in-progress" ||
        status === "completed"
      ) {
        logDebug("Setting handover state to:", status);
        dispatch(setHandoverState(status));
      } else {
        logDebug(`Ignoring invalid status: ${selectedHandover.status}`);
      }
    } else {
      logDebug("No status provided in selectedHandover");
    }
  } else {
    logDebug("No selectedHandover provided to updateShiftHandoverState");
  }
};

/**
 * Function to fetch shift handover details from API and update Redux state
 * @param selectedHandover - Selected handover record
 * @param attendees - Attendees data from Redux store
 * @param dispatch - Redux dispatch function
 * @param setIsLoading - Function to set loading state
 * @param setApiError - Function to set API error state
 */
export const fetchAndUpdateShiftHandoverDetails = async (
  selectedHandover: ShiftHandoverRecord | null,
  attendees: any[],
  dispatch: AppDispatch,
  setIsLoading: (loading: boolean) => void,
  setApiError: (error: string | null) => void
): Promise<void> => {
  if (!selectedHandover || !selectedHandover.ID) {
    logDebug("No selected handover or ID provided");
    return;
  }
  try {
    logDebug(`Fetching shift handover details for ID: ${selectedHandover.ID}`);
    setIsLoading(true);
    setApiError(null);

    // Create API client instance
    const api = createApiClient();

    // Make API call to get handover details
    const endpoint = `/shift-handover-walks/${selectedHandover.ID}`;
    const handoverDetails = await apiClient(api, endpoint, "GET");
    logDebug("Fetched handover details:", handoverDetails);

    // Check if we received valid data
    if (!handoverDetails || !handoverDetails.walkData) {
      const noDataError = new Error("No handover details data returned from API");
      
      handleShiftHandoverErrorWithFriendlyMessage(
        SHIFTHANDOVER_API_NAMES.FETCH_HANDOVER_DETAILS,
        noDataError,
        true, // Show modal overlay
        {
          handoverId: selectedHandover?.ID,
          status: selectedHandover?.status,
          reason: "Empty or invalid response data"
        }
      );
      
      setApiError("Unable to load handover details. No data returned.");
      setIsLoading(false);
      return;
    }

    // Process attendees if available
    if (handoverDetails.walkData && handoverDetails.walkData.attendees) {
      console.log(
        "Processing attendees from handover details:",
        handoverDetails.walkData.attendees
      );
      processAttendees(handoverDetails.walkData.attendees, attendees, dispatch);
    }

    // Process toolsMapping to set selected questions
    if (
      handoverDetails.toolsMapping &&
      Array.isArray(handoverDetails.toolsMapping)
    ) {
      processToolsMapping(handoverDetails.toolsMapping, dispatch);
    } // Process other handover data
    if (handoverDetails.walkData) {
      // Update observation notes if available
      if (handoverDetails.walkData.notes !== undefined) {
        dispatch(setObservationNotes(handoverDetails.walkData.notes || ""));
        logDebug("Updated observation notes from API response");
      }
      let miscellaneousObservations = [
        {
          imageUrl: handoverDetails.walkData.miscellImage,
          description: handoverDetails.walkData.miscellImageDesc,
        },
        {
          imageUrl: handoverDetails.walkData.miscellImage2,
          description: handoverDetails.walkData.miscellImage2Desc,
        },
      ]; // Process miscellaneous observations if available
      if (miscellaneousObservations !== undefined) {
        miscellaneousObservations.forEach((obs, index) => {
          dispatch(
            setMiscObservationImage({
              index,
              image: {
                imageUrl: obs.imageUrl || "",
                fileName: "",
                description: obs.description || "",
                index,
              },
            })
          );
        });
        logDebug("Updated miscellaneous observations from API response");
      }
      // Process manager signature if available
      if (handoverDetails.walkData.managerSignature) {
        logDebug(
          "Found manager signature in response:",
          handoverDetails.walkData.managerSignature
        );

        // Check if we need to construct a proper image URL
        let imageUrl = handoverDetails.walkData.managerSignature;

        dispatch(
          setSignatureFileName({
            fileName: handoverDetails.walkData.managerSignature,
            imageUrl: imageUrl,
          })
        );

        logDebug(
          "Updated manager signature from API response with imageUrl:",
          imageUrl
        );
      }

      // Update selected shift if available
      if (handoverDetails.walkData.shift) {
        const shiftData = handoverDetails.walkData.shift;
        const shiftId =
          typeof shiftData === "string"
            ? shiftData
            : shiftData.id || shiftData.ID;
        const shiftDisplayText =
          typeof shiftData === "string"
            ? shiftData
            : shiftData.displayText || shiftData.DisplayText || shiftData;

        if (shiftId) {
          dispatch(
            setSelectedShift({
              id: shiftId.toString(),
              displayText: shiftDisplayText.toString(),
            })
          );
          logDebug(
            `Updated selected shift to: ${shiftDisplayText} (ID: ${shiftId})`
          );
        }
      } // Process images if available
      if (handoverDetails.images && Array.isArray(handoverDetails.images)) {
        logDebug(
          `Processing ${handoverDetails.images.length} images from API response`
        );

        handoverDetails.images.forEach((image: any) => {
          if (!image) {
            return;
          }

          if (image.questionId) {
            // This is a question-specific image
            logDebug(`Setting handover image for question ${image.questionId}`);
            dispatch(
              setShiftHandoverImage({
                imageId: image.imageId?.toString() || Date.now().toString(),
                imageBase64: image.imageBase64 || image.imageUrl || "",
                imagePreview:
                  image.imagePreview ||
                  image.imageBase64 ||
                  image.imageUrl ||
                  "",
                description: image.description || "",
                questionId: image.questionId.toString(),
              })
            );
          } else {
            // This is a miscellaneous observation image
            logDebug(
              `Setting miscellaneous observation image at index ${
                image.index || 0
              }`
            );
            dispatch(
              setMiscObservationImage({
                index: image.index || 0,
                image: {
                  imageUrl: image.imageUrl || image.imageBase64 || "",
                  fileName: image.fileName || "",
                  description: image.description || "",
                  index: image.index || 0,
                },
              })
            );
          }
        });
      }

      // Process shiftHandoverImages if available in the new API response format
      if (
        handoverDetails.shiftHandoverImages &&
        Array.isArray(handoverDetails.shiftHandoverImages)
      ) {
        logDebug(
          `Processing ${handoverDetails.shiftHandoverImages.length} shift handover images from API response`
        );

        // Clear existing images first to prevent duplication issues
        dispatch(resetShiftHandoverImages());

        // Create a dictionary to group images by ObservationId
        const imagesByObservationId: Record<string, any[]> = {};

        // Group all images by ObservationId for debugging and processing
        handoverDetails.shiftHandoverImages.forEach((image: any) => {
          if (!image) {
            return;
          }

          const observationId = (image.ObservationId || "").toString();
          if (!imagesByObservationId[observationId]) {
            imagesByObservationId[observationId] = [];
          }
          imagesByObservationId[observationId].push(image);
        });

        // Log grouped images for debugging
        Object.keys(imagesByObservationId).forEach((observationId) => {
          const images = imagesByObservationId[observationId];
          logDebug(
            `Found ${images.length} images for ObservationId ${observationId}:`,
            images.map((img) => ({
              ImageId: img.ImageId,
              Description: img.Description,
            }))
          );
        });

        // Process each image
        handoverDetails.shiftHandoverImages.forEach((image: any) => {
          if (!image) {
            return;
          }

          logDebug(
            `Setting shift handover image with ImageId=${image.ImageId} for ObservationId=${image.ObservationId}`
          );

          // Extract file name from the URL if possible
          let fileName = "image.png"; // Default file name
          try {
            console.log("Extracting file name from image URL:", image);
            // Parse the URL to extract the filename
            if (image.ImageBase64 && image.ImageBase64.includes("http")) {
              const url = new URL(image.ImageBase64);
              console.log("Parsed URL:", url);
              const pathname = url.pathname;
              // Extract only the file name portion with extension
              const fileNameMatch = pathname.match(/\/([^/]+\.[^/.]+)(?:\?|$)/);
              if (fileNameMatch && fileNameMatch[1]) {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                fileName = fileNameMatch[1];
              }
            }
          } catch (error) {
            logError("Error extracting filename from URL:", error);
          } // Log detailed information about the image mapping for debugging
          logDebug(
            `Mapping image with: ImageId=${image.ImageId}, ObservationId=${image.ObservationId}, Description="${image.Description}"`
          );

          // Use the imageId directly from the API response
          // Only convert to string to ensure consistent type for Redux store
          const imageId = image.ImageId;

          logDebug(`Using original ImageId from response: ${imageId}`);
          dispatch(
            setShiftHandoverImage({
              imageId:
                typeof imageId === "string" || typeof imageId === "number"
                  ? imageId.toString()
                  : "1",
              imageBase64: image.ImageBase64 || "",
              imagePreview: image.ImageBase64 || "",
              description: image.Description || "",
              questionId: image.ObservationId.toString(),
            })
          );
        });
      }
    }
    setIsLoading(false);
  } catch (error: any) {
    logError("Error fetching shift handover details:", error);
    setIsLoading(false);

    // Show error overlay modal
    handleShiftHandoverErrorWithFriendlyMessage(
      SHIFTHANDOVER_API_NAMES.FETCH_HANDOVER_DETAILS,
      error,
      true, // Show modal overlay
      {
        handoverId: selectedHandover?.ID,
        status: selectedHandover?.status,
      }
    );

    // Handle different error cases
    if (error?.response?.status === 404) {
      setApiError("Shift Handover Walk not found");
    } else if (error?.response?.status === 403) {
      setApiError(
        "You do not have permission to access this Shift Handover Walk"
      );
    } else if (error?.response?.status === 401) {
      setApiError("Authentication error. Please log in again");
    } else if (error?.response?.status >= 500) {
      setApiError("Server error. Please try again later");
    } else {
      setApiError(
        error?.response?.data?.message || error?.message || "Unknown error"
      );
    }
  }
};

/**
 * Helper function to process attendees from API response
 * @param attendeesString - Comma-separated string of attendee IDs or attendee data
 * @param availableAttendees - Available attendees from Redux store
 * @param dispatch - Redux dispatch function
 */
const processAttendees = (
  attendeesString: string,
  availableAttendees: any[],
  dispatch: AppDispatch
): void => {
  if (!attendeesString) {
    logDebug("No attendees string provided");
    return;
  }

  try {
    // First check if the attendeesString is in JSON format (could be a stringified array)
    let attendeeData: any[] = [];
    try {
      const parsedData = JSON.parse(attendeesString);
      if (Array.isArray(parsedData)) {
        attendeeData = parsedData;
        logDebug("Successfully parsed attendees JSON data:", attendeeData);
      }
    } catch (parseError) {
      // Not JSON, continue with comma-separated string handling
      logDebug(
        "Attendees string is not in JSON format, treating as comma-separated IDs or emails"
      );
    }

    // If we have parsed data, use it directly
    if (attendeeData.length > 0) {
      // Map the parsed data to the expected attendee format
      const mappedAttendees = attendeeData.map((item) => ({
        id: item.id?.toString() || item.ID?.toString(),
        displayName:
          item.displayName ||
          item.DisplayName ||
          `${item.firstName || ""} ${item.lastName || ""}`.trim(),
        userPrincipalName:
          item.userPrincipalName || item.email || item.Email || "",
      }));
      logDebug("Mapped attendees from JSON data:", mappedAttendees);
      dispatch(setSelectedAttendees(mappedAttendees));
      return;
    }

    // If no JSON data, proceed with comma-separated string handling
    // Split the attendees string by comma
    const attendeeEmailsOrIds = attendeesString
      .split(",")
      .map((id) => id.trim().toLowerCase())
      .filter(Boolean);
    logDebug("Processing attendee emails/IDs:", attendeeEmailsOrIds);

    if (!attendeeEmailsOrIds.length) {
      logDebug("No attendee emails/IDs found in string");
      return;
    }

    if (!availableAttendees || !availableAttendees.length) {
      logDebug("No available attendees provided to match against emails/IDs");
      return;
    }

    // Find matching attendees in the available attendees by userPrincipalName (email) or id
    const matchedAttendees = availableAttendees
      .filter((attendee) => {
        const attendeeId =
          attendee.id?.toString().toLowerCase() ||
          attendee.ID?.toString().toLowerCase();
        const attendeeEmail = (
          attendee.userPrincipalName ||
          attendee.email ||
          attendee.Email ||
          ""
        ).toLowerCase();
        return (
          attendeeEmailsOrIds.includes(attendeeId) ||
          attendeeEmailsOrIds.includes(attendeeEmail)
        );
      })
      .map((attendee) => {
        // Format displayName as "LastName, FirstName {PEP}" if possible
        let displayName = attendee.displayName || attendee.DisplayName;
        if (!displayName && (attendee.firstName || attendee.lastName)) {
          displayName = `${attendee.lastName || ""}, ${
            attendee.firstName || ""
          } {PEP}`
            .replace(/^, | ,/g, "")
            .trim();
        }
        if (!displayName) {
          displayName =
            attendee.userPrincipalName ||
            attendee.email ||
            attendee.Email ||
            "";
        }
        return {
          id: attendee.id?.toString() || attendee.ID?.toString(),
          displayName,
          userPrincipalName:
            attendee.userPrincipalName ||
            attendee.email ||
            attendee.Email ||
            "",
        };
      });
    
    logDebug("Matched attendees from available attendees:", matchedAttendees);

    // Find unmatched attendee emails/IDs
    const matchedEmailsAndIds = new Set();
    matchedAttendees.forEach((attendee) => {
      matchedEmailsAndIds.add(attendee.id?.toLowerCase());
      matchedEmailsAndIds.add(attendee.userPrincipalName?.toLowerCase());
    });

    const unmatchedEmailsOrIds = attendeeEmailsOrIds.filter(
      (emailOrId) => !matchedEmailsAndIds.has(emailOrId)
    );

    logDebug("Unmatched attendee emails/IDs:", unmatchedEmailsOrIds);

    // Process unmatched attendees by parsing emails
    const unmatchedAttendees = unmatchedEmailsOrIds.map((emailOrId) => {
      const isEmail = emailOrId.includes("@");
      if (isEmail) {
        // Parse email to get display name
        const displayName = formatUserName(emailOrId);
        return {
          id: emailOrId, // Use email as ID for unmatched attendees
          displayName: displayName,
          userPrincipalName: emailOrId,
        };
      } else {
        // This is likely an ID that we couldn't match
        return {
          id: emailOrId,
          displayName: `User (${emailOrId})`, // Fallback display name
          userPrincipalName: emailOrId,
        };
      }
    });

    logDebug("Processed unmatched attendees:", unmatchedAttendees);

    // Combine matched and unmatched attendees
    const allSelectedAttendees = [...matchedAttendees, ...unmatchedAttendees];
    
    logDebug("All selected attendees (matched + unmatched):", allSelectedAttendees);

    // Dispatch to update selected attendees in Redux
    if (allSelectedAttendees.length > 0) {
      dispatch(setSelectedAttendees(allSelectedAttendees));
    }
  } catch (error) {
    logError("Error processing attendees:", error);
  }
};

/**
 * Helper function to process toolsMapping from API response and set selected questions
 * @param toolsMapping - Array of tool mappings from API response
 * @param dispatch - Redux dispatch function
 */
const processToolsMapping = (
  toolsMapping: { toolId: number }[],
  dispatch: AppDispatch
): void => {
  if (
    !toolsMapping ||
    !Array.isArray(toolsMapping) ||
    toolsMapping.length === 0
  ) {
    logDebug("No tools mapping provided");
    return;
  }

  try {
    logDebug(`Processing ${toolsMapping.length} tools mappings`);

    // Get current state to access the shiftHandoverQuestions
    const state = require("../../store").default.getState();
    const shiftHandoverQuestions =
      state.shiftHandOver.shiftHandoverQuestions || [];

    // Create a lookup map for faster question matching
    const questionLookup = new Map<string, any>();
    shiftHandoverQuestions.forEach((q: any) => {
      const id = q.id?.toString();
      if (id) {
        questionLookup.set(id, q);
      }
    });

    logDebug(`Found ${questionLookup.size} questions in store for mapping`);

    // Process each tool mapping
    toolsMapping.forEach((tool) => {
      try {
        const toolId = tool.toolId?.toString();
        if (!toolId) {
          logDebug("Missing toolId in tool mapping:", tool);
          return;
        }

        logDebug(`Processing tool mapping with ID: ${toolId}`);

        // Look up the question in our questions data
        const matchingQuestion = questionLookup.get(toolId);

        if (matchingQuestion) {
          logDebug(
            `Found matching question for ID ${toolId}:`,
            matchingQuestion
          );

          // Use the actual question data from the store
          dispatch(
            toggleShiftHandoverOption({
              id: toolId,
              questionText: matchingQuestion.questionText,
              category: matchingQuestion.category,
            })
          );
        } else {
          logDebug(
            `No matching question found for ID ${toolId}, using placeholder`
          );

          // If no matching question found, use placeholder data
          dispatch(
            toggleShiftHandoverOption({
              id: toolId,
              questionText: `Question #${toolId}`, // Placeholder text
              category: "Unknown", // Placeholder category
            })
          );
        }
      } catch (error) {
        logError("Error processing a tool mapping:", error);
      }
    });

    logDebug("Successfully processed tools mappings");
  } catch (error) {
    logError("Error processing tools mappings:", error);
  }
};
